import { useQuery } from "@tanstack/react-query";

import { getPracticeManagementCropParameter, getPracticeManagementCropParameters } from "services";

import { PRACTICE_MANAGEMENT, PRACTICE_MANAGEMENT_CROP_PARAMETERS } from "consts";

/**
 * @function usePracticeManagementCropParameter
 * @description Fetch the crop parameter (using fallbacks where appropriate)
 * for the specified crop (with optional version / country)
 * @param cropId Crop ID
 * @param country (Optional) country query for the practice management service
 * @returns useQuery QueryResult for the requested crop parameters
 */
export const usePracticeManagementCropParameters = (country: PracticeManagement.CountryCode = "global") =>
  useQuery({
    queryKey: [PRACTICE_MANAGEMENT, PRACTICE_MANAGEMENT_CROP_PARAMETERS, country],
    queryFn: async () => {
      const params = await getPracticeManagementCropParameters(country);
      return params.map((c) => ({
        ...c,
        editingKey: c.cropId,
      }));
    },
    staleTime: 1000 * 60 * 60, // 1h
    gcTime: 1000 * 60 * 24, // 24h
  });

/**
 * @function usePracticeManagementCropParameter
 * @description Fetch the crop parameter (using fallbacks where appropriate)
 * for the specified crop (with optional version / country)
 * @param cropId Crop ID
 * @param country (Optional) country query for the practice management service
 * @returns useQuery QueryResult for the requested crop parameters; data is null when no parameter exists for the crop
 */
export const usePracticeManagementCropParameter = (cropId: string, country: PracticeManagement.CountryCode = "global") =>
  useQuery({
    queryKey: [PRACTICE_MANAGEMENT, PRACTICE_MANAGEMENT_CROP_PARAMETERS, cropId, country],
    // react-query treats a queryFn resolving to undefined as an error, so map a missing record (404) to null
    queryFn: async () => (await getPracticeManagementCropParameter(cropId, country)) ?? null,
    enabled: !!cropId,
    staleTime: 1000 * 60 * 60, // 1h
    gcTime: 1000 * 60 * 24, // 24h
  });
